import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config';
import Swal from 'sweetalert2';
import KeyboardReturnRoundedIcon from '@mui/icons-material/KeyboardReturnRounded';
import loadingGif from '../../assets/loading.gif';
import { IconButton } from '@mui/material';
import {
  Box,
  Card,
  Typography,
  Button,
  Divider,
  LinearProgress,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
} from '@mui/material';
import './EditOrganizationMember.css';

const EditOrganizationMember = () => {
  const { memberId } = useParams();
  const [memberEmail, setMemberEmail] = useState('');
  const [memberPassword, setMemberPassword] = useState('');
  const [memberFirstName, setMemberFirstName] = useState('');
  const [memberLastName, setMemberLastName] = useState('');
  const [memberNumber, setMemberNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchMemberData = async () => {
      const token = localStorage.getItem('token');
      const orgId = localStorage.getItem('selectedOrg');

      if (!token || !orgId) {
        navigate('/unauthorized');
        return;
      }

      setLoading(true);

      try {
        const response = await axios.get(`${API_BASE_URL}/org-members/member/${memberId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            SelectedOrg: orgId,
          },
        });

        const data = response.data.memberDetails;

        setMemberEmail(data.memberEmail || '');
        setMemberFirstName(data.memberFirstName || '');
        setMemberLastName(data.memberLastName || '');
        setMemberNumber(data.memberPhoneNumber || '');

      } catch (error) {
        console.error('Error fetching member data:', error);
        Swal.fire({
          title: 'Error!',
          text: 'Failed to retrieve member information.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } finally {
        setLoading(false);
      }
    };

    fetchMemberData();
  }, [navigate, memberId]);

  const handleEditMember = async () => {
    if (!memberEmail || !memberFirstName || !memberLastName || !memberNumber) {
        Swal.fire({
          title: 'Error!',
          text: 'Please fill out all fields.',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      } else {
        try {
          const token = localStorage.getItem('token');
          const orgId = localStorage.getItem('selectedOrg');
    
          const response = await fetch(`${API_BASE_URL}/org-members/edit`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              SelectedOrg: orgId,
            },
            body: JSON.stringify({
              memberEmail: memberEmail,
              memberPassword: memberPassword ? memberPassword : -1,
              memberFirstName,
              memberLastName,
              memberNumber,
            }),
          });
    
          if (response.status === 201) {
            const data = await response.json();
    
            Swal.fire({
              title: 'Success!',
              text: 'Member updated successfully!',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              navigate('/organization/members');
            });
          } else if (response.status === 409) {
            Swal.fire({
              title: 'Conflict',
              text: 'The member already exists in the organization.',
              icon: 'warning',
              confirmButtonText: 'OK',
            });
          } else {
            throw new Error('Failed to edit member');
          }
    
        } catch (error) {
          console.error('Error creating member:', error);
          Swal.fire({
            title: 'Error!',
            text: 'Failed to edit member.',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        }
    };
  }

  if (loading) {
    return (
      <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
          backgroundColor: '#fff',
      }}>
          <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
      </div>
      );
    }

  return (
  <Box sx={{ width: '100%', margin: 'auto', mt: 0 }}>
    <Card sx={{ width: '100%', mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
              <IconButton onClick={() => navigate('/organization/members')} sx={{ mr: 2 }}>
                <KeyboardReturnRoundedIcon />
              </IconButton>
              <Typography variant="h5" sx={{ flexGrow: 1 }}>
                Edit Organization Member
              </Typography>
            </Box>
            <Divider />
        <Grid item xs={12} md={12} sx={{ mt: 3 }}>
          <div className="input-container">
            <Typography variant="body1" sx={{ mb: 0 }}>
              Memeber Email
            </Typography>
            <input
              type="text"
              value={memberEmail}
              disabled
              placeholder="Email"
              maxLength={255}
            />

            <Typography variant="body1" sx={{ mb: 0 }}>
              Member Password (leave blank if not updating)
            </Typography>
            <div className="password-container">
              <input
                type={showPassword ? 'text' : 'password'}
                value={memberPassword}
                onChange={(e) => setMemberPassword(e.target.value)}
                placeholder="Password"
                maxLength={255}
              />
              <button
                type="button"
                className="show-password-button"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <Typography variant="body1" sx={{ mb: 0 }}>
              Member First Name
            </Typography>
            <input
              type="text"
              value={memberFirstName}
              onChange={(e) => setMemberFirstName(e.target.value)}
              placeholder="First Name"
              maxLength={255}
            />

            <Typography variant="body1" sx={{ mb: 0 }}>
              Member Last Name
            </Typography>
            <input
              type="text"
              value={memberLastName}
              onChange={(e) => setMemberLastName(e.target.value)}
              placeholder="Last Name"
              maxLength={255}
            />

            <Typography variant="body1" sx={{ mb: 0 }}>
              Member Phone Number
            </Typography>
            <input
              type="text"
              value={memberNumber}
              onChange={(e) => setMemberNumber(e.target.value)}
              placeholder="Phone Number"
              maxLength={20}
            />
          </div>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            onClick={handleEditMember}
            disabled={loading}
          >
            Update Member
          </Button>
        </Grid>
    </Card>
  </Box>
);
};

export default EditOrganizationMember;
